import type { AdventureMode } from '../../services/GameApiService';

/**
 * Visual cue for the gameplay mode of an adventure, shown on the public
 * catalog (`game.selna.ai/`) and on the adventure detail page.
 *
 * Uses unicode emoji glyphs as the visual primitive — keeps the icon
 * recognisable at a glance and avoids any bespoke SVG that drifts out of
 * sync with the emoji vocabulary players already know:
 *
 *   linear       → 🎞️  film frames     "linear, one scene after another"
 *   exploration  → 📖  open book        "read the story, person by person"
 *   mystery      → 🔍  magnifying glass "investigate, gather clues"
 *
 * Two presentation variants:
 *   - 'circle'  : 56×56 circular pill used on the catalog card hero image,
 *                 replaces the previous initial-character avatar.
 *   - 'pill'    : horizontal chip with icon + label, used on the detail page
 *                 hero next to the existing episode/role/tone chips.
 *
 * Accessibility:
 *   - The mode name is rendered as visible text in the `pill` variant.
 *   - For the `circle` variant (no visible label), the `aria-label` /
 *     `title` describes the mode in plain Portuguese so screen-reader
 *     users and mouse hover get the same context.
 */

export type AdventureModeBadgeVariant = 'circle' | 'pill';

interface AdventureModeBadgeProps {
  /** Internal mode flag. null = legacy adventure, treated as whodunnit. */
  mode: AdventureMode;
  /** Visual presentation. Defaults to 'circle' for catalog cards. */
  variant?: AdventureModeBadgeVariant;
  /** Optional extra classes (e.g. positioning) merged into the wrapper. */
  className?: string;
}

interface ModePresentation {
  /** Short PT-PT label shown in the `pill` variant. */
  label: string;
  /** Hover tooltip and screen-reader description. */
  description: string;
  /** Emoji glyph rendered as the visual primitive. */
  glyph: string;
}

/**
 * Legacy adventures (created before mysteryMode was a field) are surfaced
 * to players as Mistério — that was the historical default and changing
 * it now would surprise returning users on already-published content.
 */
function resolveMode(mode: AdventureMode): 'linear' | 'exploration' | 'mystery' {
  return mode === 'linear' || mode === 'exploration' ? mode : 'mystery';
}

const MODE_PRESENTATION: Record<'linear' | 'exploration' | 'mystery', ModePresentation> = {
  linear: {
    label: 'História',
    description:
      'História — recebes a história em ordem fixa, não escolhes com quem falar',
    glyph: '🎞️',
  },
  exploration: {
    label: 'Exploração',
    description:
      'Exploração — escolhes com quem falar; cada pessoa dá-te a sua parte da história, sem mistério a resolver',
    glyph: '📖',
  },
  mystery: {
    label: 'Mistério',
    description:
      'Mistério — escolhes com quem falar, cruzas pistas e eliminas suspeitos até descobrir quem foi',
    glyph: '🔍',
  },
};

export default function AdventureModeBadge({
  mode,
  variant = 'circle',
  className,
}: AdventureModeBadgeProps) {
  const resolved = resolveMode(mode);
  const { label, description, glyph } = MODE_PRESENTATION[resolved];

  if (variant === 'pill') {
    return (
      <span
        className={
          'inline-flex items-center gap-2 px-3 py-1 rounded-full ' +
          'bg-white/15 backdrop-blur-sm text-white text-sm md:text-base ' +
          (className ?? '')
        }
        aria-label={description}
      >
        <span aria-hidden="true" className="text-base md:text-lg leading-none">
          {glyph}
        </span>
        <span>{label}</span>
      </span>
    );
  }

  // 'circle' variant — sits on the dark hero image, replaces the previous
  // initial-character avatar. The glyph is sized to fit comfortably inside
  // the 56px capsule with a hint of breathing room.
  return (
    <span
      className={
        'absolute bottom-4 left-4 w-14 h-14 rounded-full overflow-hidden ' +
        'border-2 border-white shadow-lg bg-black/40 backdrop-blur-sm ' +
        'flex items-center justify-center ' +
        (className ?? '')
      }
      title={description}
      aria-label={description}
      role="img"
    >
      <span aria-hidden="true" className="text-2xl leading-none -mt-0.5">
        {glyph}
      </span>
    </span>
  );
}
